import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { COUNTRY_NAMES, fmt } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Rocket, AlertTriangle, CheckCircle2, CalendarClock, Sparkles, Bot } from "lucide-react";

const COUNTRIES = Object.keys(COUNTRY_NAMES).sort();

export default function CampaignBuilder() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [meta, setMeta] = useState(null);
  const [audience, setAudience] = useState({ count: 0, by_country: {} });
  const [preflight, setPreflight] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    target_countries: [],
    target_tags: [],
    target_lists: [],
    message_variants: ["Bonjour {first_name}, ", "Salut {first_name}, "],
    media_url: "",
    send_window_start: "09:00",
    send_window_end: "20:00",
    spread_duration_hours: 8,
    scheduled_for: "",
    auto_reply_enabled: false,
    auto_reply_prompt: "",
    max_ai_exchanges: 5,
    ignore_recent_24h: false,
    verified_only: true,
  });
  const [generating, setGenerating] = useState(null);

  useEffect(() => { api.get("/contacts/meta").then((r) => setMeta(r.data)); }, []);

  useEffect(() => {
    if (!editId) return;
    api.get(`/campaigns/${editId}`).then(({ data }) => {
      setForm({
        name: data.name,
        target_countries: data.target_countries || [],
        target_tags: data.target_tags || [],
        target_lists: data.target_lists || [],
        message_variants: data.message_variants || ["", ""],
        media_url: data.media_url || "",
        send_window_start: data.send_window_start,
        send_window_end: data.send_window_end,
        spread_duration_hours: data.spread_duration_hours,
        scheduled_for: data.scheduled_for ? data.scheduled_for.slice(0, 16) : "",
        auto_reply_enabled: !!data.auto_reply_enabled,
        auto_reply_prompt: data.auto_reply_prompt || "",
        max_ai_exchanges: data.max_ai_exchanges || 5,
        ignore_recent_24h: !!data.ignore_recent_24h,
        verified_only: data.verified_only !== false,
      });
    }).catch(() => toast.error("Campagne introuvable"));
  }, [editId]);

  useEffect(() => {
    api.post("/contacts/audience-count", {
      countries: form.target_countries, tags: form.target_tags, lists: form.target_lists,
      ignore_recent_24h: form.ignore_recent_24h,
      verified_only: form.verified_only,
    }).then((r) => setAudience(r.data));
  }, [form.target_countries, form.target_tags, form.target_lists, form.ignore_recent_24h,
      form.verified_only]);

  const toggle = (key, value) => {
    const list = form[key];
    setForm({ ...form, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] });
  };

  const setVariant = (i, value) => {
    const v = [...form.message_variants];
    v[i] = value;
    setForm({ ...form, message_variants: v });
  };

  const createAndCheck = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form, media_url: form.media_url || null,
        media_type: form.media_url ? "media" : null,
        scheduled_for: form.scheduled_for ? new Date(form.scheduled_for).toISOString() : null,
      };
      const campaignId = editId
        ? (await api.put(`/campaigns/${editId}`, payload)).data.id
        : (await api.post("/campaigns", payload)).data.id;
      const pf = await api.post(`/campaigns/${campaignId}/preflight`);
      setPreflight({ ...pf.data, campaign_id: campaignId });
      toast.success(editId ? "Modifications enregistrées" : "Campagne enregistrée en brouillon");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const launch = async (force = false) => {
    try {
      await api.post(`/campaigns/${preflight.campaign_id}/launch`, null, { params: { force } });
      toast.success("Campagne lancée");
      navigate(`/campagnes/${preflight.campaign_id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Lancement impossible");
    }
  };

  const schedule = async () => {
    try {
      await api.post(`/campaigns/${preflight.campaign_id}/schedule`, {
        scheduled_for: new Date(form.scheduled_for).toISOString(),
      });
      toast.success("Campagne planifiée");
      navigate(`/campagnes/${preflight.campaign_id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Planification impossible");
    }
  };

  const toggleIn = null;

  const generateVariant = async (index) => {    const base = form.message_variants.find((v) => v.trim().length >= 10);
    if (!base) {
      toast.error("Rédigez d'abord une première variante (10 caractères minimum)");
      return;
    }
    setGenerating(index);
    try {
      const { data } = await api.post("/ai/variants", {
        base_message: base, count: 1, name: form.name,
        target_countries: form.target_countries,
      });
      const variants = [...form.message_variants];
      variants[index] = data.variants[0];
      setForm({ ...form, message_variants: variants });
      toast.success("Variante générée");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Génération impossible");
    } finally {
      setGenerating(null);
    }
  };

  const preview = (text) => text.replace("{first_name}", "Marie");
  const estHours = form.spread_duration_hours;

  return (
    <Layout title={editId ? "Modifier la campagne" : "Nouvelle campagne"}
      subtitle="Construction, audience et contrôle de capacité">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="campaign-builder-page">
        <div className="lg:col-span-2 space-y-5">
          <section className="card-tactical p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">1 · Identification</h2>
            <input data-testid="campaign-name-input" placeholder="Nom de la campagne" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
          </section>

          <section className="card-tactical p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">2 · Audience</h2>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500">Listes (une liste peut être multi-pays)</p>
              <button onClick={() => navigate("/listes")} data-testid="manage-lists-link"
                className="text-xs text-emerald-700 underline">Gérer les listes</button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="list-selector">
              {(meta?.lists || []).length === 0 && (
                <p className="text-xs text-gray-400">
                  Aucune liste pour l'instant — créez-en une depuis l'onglet Listes, puis assignez-y
                  des contacts.
                </p>
              )}
              {(meta?.lists || []).map((l) => (
                <button key={l.id} onClick={() => toggle("target_lists", l.id)}
                  data-testid={`list-chip-${l.id}`}
                  className={`rounded-full px-3 py-1.5 text-xs border transition-colors duration-200 ${
                    form.target_lists.includes(l.id)
                      ? "bg-emerald-500 text-white border-emerald-500"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                  {l.name}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4 mb-2">
              Pays ciblés (aucun = tous les pays des contacts visés)
            </p>
            <div className="flex flex-wrap gap-2" data-testid="country-selector">
              {(meta?.countries?.length ? meta.countries : COUNTRIES).map((c) => (
                <button key={c} onClick={() => toggle("target_countries", c)}
                  data-testid={`country-chip-${c}`}
                  className={`rounded-full px-3 py-1.5 text-xs border transition-colors duration-200 ${
                    form.target_countries.includes(c)
                      ? "bg-emerald-500 text-white border-emerald-500"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                  {COUNTRY_NAMES[c] || c}
                </button>
              ))}
            </div>
            {meta?.tags?.length > 0 && (
              <>
                <p className="text-xs text-gray-500 mt-4 mb-2">Tags</p>                <div className="flex flex-wrap gap-2" data-testid="tag-selector">
                  {meta.tags.map((t) => (
                    <button key={t} onClick={() => toggle("target_tags", t)}
                      data-testid={`tag-chip-${t}`}
                      className={`rounded-full px-3 py-1.5 text-xs border transition-colors duration-200 ${
                        form.target_tags.includes(t)
                          ? "bg-emerald-500 text-white border-emerald-500"
                          : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>

          <section className="card-tactical p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">3 · Variantes de message</h2>
              <button onClick={() => setForm({ ...form, message_variants: [...form.message_variants, ""] })}
                data-testid="add-variant-btn"
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs hover:bg-gray-50">
                <Plus className="h-3.5 w-3.5" /> Variante
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Minimum 2 variantes. Utilisez <code className="mono text-emerald-700">{"{first_name}"}</code> pour personnaliser.
            </p>
            <div className="space-y-4">
              {form.message_variants.map((v, i) => (
                <div key={i} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="mono text-xs text-gray-500">VARIANTE {i + 1}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => generateVariant(i)} disabled={generating === i}
                        data-testid={`generate-variant-${i}`}
                        className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                        <Sparkles className="h-3 w-3" />
                        {generating === i ? "Génération…" : "Générer par IA"}
                      </button>
                      {form.message_variants.length > 2 && (
                        <button onClick={() => setForm({ ...form, message_variants: form.message_variants.filter((_, j) => j !== i) })}
                          data-testid={`remove-variant-${i}`} className="text-gray-400 hover:text-red-500">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <textarea data-testid={`variant-input-${i}`} rows={3} value={v}
                    onChange={(e) => setVariant(i, e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
                  <div className="mt-2 rounded-lg bg-[#E7F7EF] p-3">
                    <p className="text-[11px] text-emerald-800 mb-1">Aperçu</p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{preview(v) || "—"}</p>
                  </div>
                </div>
              ))}
            </div>
            <input data-testid="media-url-input" placeholder="URL média (image ou PDF) — optionnel" value={form.media_url}
              onChange={(e) => setForm({ ...form, media_url: e.target.value })}
              className="mt-4 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
          </section>

          <section className="card-tactical p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">4 · Rythme d'envoi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="text-sm text-gray-700">Début de fenêtre
                <input data-testid="window-start-input" type="time" value={form.send_window_start}
                  onChange={(e) => setForm({ ...form, send_window_start: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono" />
              </label>
              <label className="text-sm text-gray-700">Fin de fenêtre
                <input data-testid="window-end-input" type="time" value={form.send_window_end}
                  onChange={(e) => setForm({ ...form, send_window_end: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono" />
              </label>
              <label className="text-sm text-gray-700">Étalement (heures)
                <input data-testid="spread-input" type="number" min="1" value={form.spread_duration_hours}
                  onChange={(e) => setForm({ ...form, spread_duration_hours: Number(e.target.value) })}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono" />
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Les fenêtres sont appliquées en heure locale du pays de chaque contact. Les délais entre
              envois sont aléatoires (45 à 180 s) par instance.
            </p>
            <label className="mt-4 flex items-start gap-2.5 rounded-lg border border-gray-200 p-3 cursor-pointer">
              <input type="checkbox" data-testid="ignore-recent-toggle" className="mt-0.5"
                checked={form.ignore_recent_24h}
                onChange={(e) => setForm({ ...form, ignore_recent_24h: e.target.checked })} />
              <span className="text-xs text-gray-600">
                <strong className="text-gray-900">Ignorer le délai de 24 h</strong> — par défaut, un
                contact déjà messagé dans les dernières 24 h est exclu (protection anti-sur-sollicitation).
                À n'activer que pour vos tests.
              </span>
            </label>
            <label className="mt-3 flex items-start gap-2 cursor-pointer">
              <input type="checkbox" data-testid="verified-only-toggle" className="mt-0.5"
                checked={form.verified_only}
                onChange={(e) => setForm({ ...form, verified_only: e.target.checked })} />
              <span className="text-xs text-gray-600">
                <strong className="text-gray-900">N'envoyer qu'aux numéros vérifiés</strong> —
                seuls les contacts confirmés comme ayant WhatsApp sont ciblés. Les autres restent
                en attente jusqu'à leur vérification automatique, ce qui évite de brûler votre
                numéro sur des numéros morts.
                {audience?.exclusions?.unverified > 0 && (
                  <span className="block mt-1 text-amber-800">
                    {audience.exclusions.unverified} contact(s) de cette cible ne sont pas encore
                    vérifiés.
                  </span>
                )}
              </span>
            </label>
            <div className="mt-4 border-t border-gray-100 pt-4">
              <label className="text-sm text-gray-700">Planifier le lancement (optionnel)
                <input data-testid="schedule-input" type="datetime-local" value={form.scheduled_for}
                  onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
                  className="mt-1 w-full sm:w-72 rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono" />
              </label>
              <p className="text-xs text-gray-500 mt-2">
                Laissez vide pour lancer immédiatement. Sinon la plateforme génère la file et démarre
                seule à l'heure indiquée (heure de votre navigateur).
              </p>
            </div>
          </section>

          <section className="card-tactical p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Bot className="h-4 w-4 text-emerald-600" /> 5 · Réponses automatiques par IA
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Si un contact répond et que sa <strong>dernière</strong> campagne reçue est
                  celle-ci, l'IA répond automatiquement dans la langue du message reçu.
                </p>
              </div>
              <button onClick={() => setForm({ ...form, auto_reply_enabled: !form.auto_reply_enabled })}
                data-testid="auto-reply-toggle"
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-medium transition-colors duration-200 ${
                  form.auto_reply_enabled ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-600"}`}>
                {form.auto_reply_enabled ? "Activées" : "Désactivées"}
              </button>
            </div>
            {form.auto_reply_enabled && (
              <div className="mt-4">
                <textarea data-testid="auto-reply-prompt-input" rows={5}
                  value={form.auto_reply_prompt}
                  onChange={(e) => setForm({ ...form, auto_reply_prompt: e.target.value })}
                  placeholder={"Contexte à donner à l'IA : nature de la campagne, informations utiles, ce qu'elle peut ou ne peut pas dire, quand renvoyer vers un conseiller humain…"}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
                <div className="mt-3 flex items-center gap-3">
                  <label className="text-sm text-gray-700">Échanges IA maximum avant passage à un humain</label>
                  <input type="number" min={1} max={20} data-testid="max-ai-exchanges-input"
                    value={form.max_ai_exchanges}
                    onChange={(e) => setForm({ ...form, max_ai_exchanges: Number(e.target.value) })}
                    className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-sm mono" />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  L'IA entretient la conversation sur plusieurs messages, puis passe la main :
                  dès que la demande devient précise (prix, rendez-vous, engagement, réclamation)
                  ou que le plafond d'échanges est atteint, le contact reçoit un message d'attente
                  dans sa langue et une <strong>alerte d'intervention</strong> apparaît sur le
                  tableau de bord et dans la boîte de réception.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Garde-fous : aucune réponse après un STOP, jamais deux réponses coup sur coup,
                  IA suspendue sur une conversation tant qu'une alerte est ouverte, et jamais si la
                  dernière campagne reçue par le contact n'a pas cette option activée.
                </p>
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <div className="card-tactical p-5 lg:sticky lg:top-28" data-testid="prelaunch-summary">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Récapitulatif</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Audience éligible</span>
                <span className="mono font-medium" data-testid="audience-count">{fmt(audience.count)}</span></div>
              {audience.exclusions?.excluded_recent_24h > 0 && (
                <div className="flex justify-between text-xs" data-testid="excluded-recent">
                  <span className="text-amber-700">Exclus (contactés &lt; 24 h)</span>
                  <span className="mono text-amber-700">{fmt(audience.exclusions.excluded_recent_24h)}</span>
                </div>
              )}
              {audience.exclusions?.excluded_status > 0 && (
                <div className="flex justify-between text-xs" data-testid="excluded-status">
                  <span className="text-gray-500">Exclus (désinscrits/invalides)</span>
                  <span className="mono text-gray-500">{fmt(audience.exclusions.excluded_status)}</span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-gray-500">Variantes</span>
                <span className="mono">{form.message_variants.filter((v) => v.trim()).length}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Étalement</span>
                <span className="mono">{estHours} h</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Fenêtre locale</span>
                <span className="mono">{form.send_window_start}–{form.send_window_end}</span></div>
            </div>

            {Object.keys(audience.by_country).length > 0 && (
              <div className="mt-4 border-t border-gray-100 pt-3 space-y-1.5">
                {Object.entries(audience.by_country).map(([c, n]) => (
                  <div key={c} className="flex justify-between text-xs">
                    <span className="text-gray-500">{COUNTRY_NAMES[c] || c}</span>
                    <span className="mono">{fmt(n)}</span>
                  </div>
                ))}
              </div>
            )}

            {!preflight ? (
              <button onClick={createAndCheck} disabled={!form.name || saving} data-testid="check-capacity-btn"
                className="mt-5 w-full rounded-lg bg-ink px-4 py-2.5 text-sm font-medium text-white hover:bg-inkSoft disabled:opacity-40">
                {editId ? "Enregistrer et vérifier la capacité" : "Vérifier la capacité"}
              </button>
            ) : (
              <div className="mt-5 space-y-3" data-testid="preflight-result">
                <div className={`rounded-lg border p-3 ${preflight.problems.length ? "border-red-300 bg-red-50" : preflight.warnings?.length ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`}>
                  <div className="flex items-start gap-2">
                    {preflight.problems.length || preflight.warnings?.length
                      ? <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                      : <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />}
                    <div className="text-xs">
                      <p className="font-medium">
                        Capacité restante aujourd'hui : <span className="mono">{fmt(preflight.total_capacity)}</span> pour{" "}
                        <span className="mono">{fmt(preflight.total_contacts)}</span> messages
                      </p>
                      {preflight.problems.map((p, i) => <p key={i} className="mt-1 text-red-700">{p}</p>)}
                      {(preflight.warnings || []).map((p, i) => <p key={i} className="mt-1 text-amber-900">{p}</p>)}
                      {!preflight.problems.length && !(preflight.warnings || []).length &&
                        <p className="mt-1 text-emerald-800">Capacité suffisante, envoi sur la journée.</p>}
                    </div>
                  </div>
                </div>

                {(preflight.suggestions || []).length > 0 && (
                  <div className="rounded-lg border border-gray-200 bg-white p-3" data-testid="capacity-suggestions">
                    <p className="text-xs font-medium text-gray-800 mb-1.5">Comment compléter la capacité</p>
                    <ul className="space-y-1.5">
                      {preflight.suggestions.map((s, i) => (
                        <li key={i} className="text-xs text-gray-600">• {s}</li>
                      ))}
                    </ul>
                    <p className="mono mt-2 text-xs text-gray-800">
                      Étalement automatique : {preflight.days_needed} jour(s)
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button onClick={() => navigate("/instances")} data-testid="add-instances-btn"
                        className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-100">
                        Ajouter une instance
                      </button>
                      <button onClick={() => setForm({ ...form, spread_duration_hours: Math.min(24, form.spread_duration_hours + 4) })}
                        data-testid="extend-spread-btn"
                        className="rounded-lg border border-gray-200 px-3 py-2 text-xs hover:bg-gray-50">
                        Étendre l'étalement (+4 h)
                      </button>
                    </div>
                  </div>
                )}

                {preflight.problems.length > 0 && (
                  <p className="text-xs text-red-700">
                    Aucune instance active pour au moins un pays ciblé : ces messages resteront bloqués.
                    Ajoutez une instance pour ce pays avant de lancer.
                  </p>
                )}
                {!preflight.problems.length && preflight.days_needed > 1 && (
                  <p className="text-xs text-gray-500">
                    Le surplus sera automatiquement replanifié sur les jours suivants, dans la fenêtre
                    d'envoi locale et sans jamais dépasser les quotas.
                  </p>
                )}
                <button onClick={() => launch(preflight.problems.length > 0)} data-testid="launch-campaign-btn"
                  className="w-full rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 flex items-center justify-center gap-2">
                  <Rocket className="h-4 w-4" /> Lancer la campagne
                </button>
                {form.scheduled_for && (
                  <button onClick={schedule} data-testid="schedule-campaign-btn"
                    className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-50 flex items-center justify-center gap-2">
                    <CalendarClock className="h-4 w-4" /> Planifier pour le{" "}
                    {new Date(form.scheduled_for).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </button>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    </Layout>
  );
}
